import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Stack,
  Typography,
} from '@mui/material';
import { PurpleAlert, PurpleArrowLeft } from 'Images';
import { useContext, useEffect, useState } from 'react';
import { Loading } from './Loading';
import { DocumentsContext, IExtendedDocument } from 'Context/DocumentsContext';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { useLoader } from 'Hooks/useLoader';
import { getDocuments } from 'API/documents';
import { DOCUMENTS_REDUCER_CASE } from 'Enum/documents';
import { DocumentDetail } from '../../AccordionDocument/DocumentDetail';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';

interface IPastYearsDocumentAccordion {
  time: string;
}

export const PastYearsDocumentAccordion = ({
  time,
}: IPastYearsDocumentAccordion) => {
  const { documents, dispatchDocuments, types } = useContext(DocumentsContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [pastDocuments, setPastDocuments] = useState<IExtendedDocument[]>([]);

  const { isLoading, setIsLoading } = useLoader();

  useEffect(() => {
    const updatedPastYearDocuments: IExtendedDocument[] = [];
    for (const doc of documents.previousYearsResults) {
      if (new Date(doc.documentDate).getFullYear() === Number(time))
        updatedPastYearDocuments.push(doc);
    }
    setPastDocuments(updatedPastYearDocuments);
  }, [documents.previousYearsResults, time]);

  const getPastYearDocumentsHandler = async () => {
    if (pastDocuments.length > 0) setIsOpen(!isOpen);
    else if (!isOpen) {
      setIsOpen(true);
      setIsLoading(true);
      const pastYearDocumentsResult = await getDocuments({
        fromDate: `${time}-1-1`,
        toDate: `${time}-12-31`,
      });
      if (pastYearDocumentsResult) {
        dispatchDocuments({
          type: DOCUMENTS_REDUCER_CASE.INIT_PAST_YEARS,
          payload: {
            value: {
              docs: pastYearDocumentsResult,
              types: types,
            },
          },
        });
      }
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    } else setIsOpen(false);
  };

  return (
    <Accordion
      expanded={isOpen}
      onClick={getPastYearDocumentsHandler}
      onKeyDown={onEnterOrEscapeKeyDown}
      sx={{
        pb: isOpen ? '48px' : undefined,
        boxShadow: 'none',
        borderRadius: '0 16px 16px 0',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={
          <Avatar
            alt=''
            src={PurpleArrowLeft}
            sx={{
              width: '20px',
              height: '20px',
              transform: 'rotate(-90deg)',
            }}
          />
        }
        sx={{
          p: '0 24px',
          height: '79px',
          bgcolor: '#F4E4FF',
          borderRadius: '0 16px 16px 0',
          borderLeft: isOpen ? '3px solid #D59DFF' : undefined,
          borderBottom: '1px solid #E4C1FF',
        }}
      >
        <Typography variant='h6'>{time}</Typography>
      </AccordionSummary>
      <AccordionDetails
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
        }}
        sx={{
          p: '0 24px',
          borderLeft: '3px solid #D59DFF',
        }}
      >
        {isLoading ? (
          <Loading />
        ) : (
          <>
            {pastDocuments.length === 0 ? (
              <Stack direction='row' alignItems='center' gap='6px' p='20px 0'>
                <Avatar
                  alt=''
                  src={PurpleAlert}
                  sx={{ width: '22px', height: '22px' }}
                />
                <Typography variant='body2'>
                  {Dictionary[DICTIONARY.Documents].noPreviousYearsDocuments}
                </Typography>
              </Stack>
            ) : (
              pastDocuments.map((documentItem, index: number) => {
                return <DocumentDetail key={index} data={documentItem} />;
              })
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
